import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import PosterCard from "./PosterCard";
import LovedByStack from "./LovedByStack";
import { useTheme } from "../context/ThemeContext";

const FALLBACK = "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?crop=entropy&cs=srgb&fm=jpg&w=700&q=80";

function StaffPickStandee({ item, testid }) {
  const navigate = useNavigate();
  return (
    <button className="vs-standee group/standee flex-none" onClick={() => navigate(`/title/${item.id}`)} data-testid={testid} title={`Staff Pick: ${item.title}`}>
      <span className="vs-standee-topper font-head">★ Staff Pick</span>
      <span className="vs-standee-art">
        <img src={item.poster || item.img} alt={item.title} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
        <span className="vs-standee-rating"><Star size={10} className="fill-current" /> {(item.rating || 0).toFixed(1)}</span>
      </span>
      <span className="vs-standee-base" />
    </button>
  );
}

export default function PosterRow({ title, items, testid, seeAll }) {
  const ref = useRef(null);
  const { layout, skin } = useTheme();
  if (!items || items.length === 0) return null;

  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  const aisle = layout.nav === "aisle"; // videostore: hanging aisle sign header
  const plank = layout.card === "box" || layout.card === "shelf"; // wooden/glass shelf under row
  const videostore = skin === "videostore";
  const staffPick = videostore && items[0]?.source !== "podcast" ? items.reduce((a, b) => ((b.rating || 0) > (a.rating || 0) ? b : a), items[0]) : null;

  return (
    <section className="group/row relative py-4" data-testid={testid}>
      {aisle ? (
        <div className="mb-4 px-4 lg:px-14">
          <span className="aisle-sign inline-block -rotate-1 border-2 border-[var(--border-accent)] bg-[var(--bg-card)] px-4 py-1 font-head text-xl uppercase tracking-widest text-[var(--accent-hover)] shadow-md">{title}</span>
        </div>
      ) : (
        <h2 className="row-heading mb-3 px-4 lg:px-14 font-head text-2xl sm:text-3xl text-[var(--text-primary)]">{title}</h2>
      )}
      {seeAll && (
        <Link to={seeAll} className="absolute right-4 top-6 z-10 text-xs font-bold uppercase tracking-widest text-[var(--accent-hover)] hover:underline lg:right-14" data-testid={`${testid}-see-all`}>See all</Link>
      )}

      <div className={`relative ${videostore ? "shelf-bay" : ""}`}>
        <button onClick={() => scroll(-1)} className="row-arrow absolute left-0 top-0 z-40 hidden h-full w-14 items-center justify-center bg-gradient-to-r from-[var(--bg-base)] to-transparent opacity-0 group-hover/row:opacity-100 md:flex" data-testid={`${testid}-scroll-left`} aria-label="Scroll left">
          <ChevronLeft className="text-[var(--text-primary)]" size={30} />
        </button>

        <div ref={ref} className="scrollbar-none flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth px-4 lg:px-14 pb-2">
          {items.map((it) => (it.loved_by?.length
            ? <div key={it.id} className="relative flex-none"><PosterCard item={it} /><LovedByStack item={it} /></div>
            : <PosterCard key={it.id} item={it} />))}
          {videostore && staffPick && <StaffPickStandee item={staffPick} testid={`${testid}-staff-pick`} />}
        </div>

        {videostore ? (
          <div className="shelf-plank mx-4 lg:mx-14" data-testid={`${testid}-shelf`}>
            <span className="shelf-tag font-head">{title}</span>
          </div>
        ) : plank ? (
          <div className="shelf-plank mx-4 lg:mx-14 h-1.5 rounded-full bg-gradient-to-b from-[color:var(--accent)]/30 to-transparent" />
        ) : null}

        <button onClick={() => scroll(1)} className="row-arrow absolute right-0 top-0 z-40 hidden h-full w-14 items-center justify-center bg-gradient-to-l from-[var(--bg-base)] to-transparent opacity-0 group-hover/row:opacity-100 md:flex" data-testid={`${testid}-scroll-right`} aria-label="Scroll right">
          <ChevronRight className="text-[var(--text-primary)]" size={30} />
        </button>
      </div>
    </section>
  );
}

